import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post, put, del, b64uToBytes } from '/api.js';
import { signOut } from '/auth.js';
import { Icon } from '/icons.js';
import { TextSize } from '/textsize.js';
import { WidthMode } from '/desktop.js';
import { Notifications } from '/notify.js';
import { useQrSvg } from '/qr.js';
import { ImageUpload, Picture } from '/imageupload.js';

/**
 * The Settings tab.
 *
 * The mockup's rows are mostly toggles for things that do not exist here —
 * notifications, themes, NFC. What is on this screen is what there actually is
 * to do: who you are, linking your league record, inviting somebody, and
 * leaving. Rows that would lie are not worth the symmetry.
 */


/**
 * Your passkeys, and adding one to this device.
 *
 * Adding requires proving you already hold a passkey — the browser is asked to
 * assert with an existing one first, which on a fresh PC means the phone, over
 * the QR flow the platform provides. A session alone is deliberately not
 * enough: a stolen one would otherwise become permanent access.
 */
function Devices() {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function reload() { setDevices(await get('/api/me/devices')); }
  useEffect(() => { reload().catch((e) => setError(e.detail || e.message)); },
            []);

  async function addThisDevice() {
    setError(null);
    setBusy(true);
    try {
      // 1. Prove you already have one.
      const opts = await post('/api/me/devices/challenge');
      opts.challenge = b64uToBytes(opts.challenge);
      if (opts.allowCredentials) {
        opts.allowCredentials.forEach((c) => { c.id = b64uToBytes(c.id); });
      }
      const assertion = await navigator.credentials.get({ publicKey: opts });

      // 2. Which returns the options for registering this one.
      const reg = await post('/api/me/devices/verify',
                             { credential: assertion.toJSON() });
      reg.challenge = b64uToBytes(reg.challenge);
      reg.user.id = b64uToBytes(reg.user.id);
      if (reg.excludeCredentials) {
        reg.excludeCredentials.forEach((c) => { c.id = b64uToBytes(c.id); });
      }
      const created = await navigator.credentials.create({ publicKey: reg });

      // 3. And this device is on the account.
      await post('/api/me/devices/add', { credential: created.toJSON() });
      await reload();
    } catch (e) {
      setError(e.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (devices === null) return null;

  return html`
    <div class="stack" data-testid="settings-devices">
      <label>Your devices</label>
      ${error && html`
        <p class="sub" data-testid="devices-error"
           style="color:var(--danger)">${error}</p>`}
      ${devices.map((d) => html`
        <div key=${d.credential_id} class="card rowbetween"
             data-testid=${`device-${d.credential_id}`}>
          <span class="sub">${d.credential_id.slice(0, 12)}…</span>
          <button class="secondary" data-testid="device-revoke"
                  onClick=${async () => {
                    setError(null);
                    try {
                      await del(`/api/me/devices/${encodeURIComponent(d.credential_id)}`);
                      await reload();
                    } catch (e) { setError(e.detail || e.message); }
                  }}>Remove</button>
        </div>`)}
      <button class="secondary" data-testid="device-add" disabled=${busy}
              onClick=${addThisDevice}>
        ${busy ? 'Waiting for your passkey…' : 'Add this device'}
      </button>
    </div>`;
}

function Row({ icon, label, value, onClick, testid, danger }) {
  return html`
    <button class="secondary card rowbetween" data-testid=${testid}
            onClick=${onClick}
            style=${`width:100%;margin:0 0 .375rem;text-align:left;
                     text-transform:none;letter-spacing:normal;
                     font-family:var(--body);font-size:.875rem;
                     ${danger ? 'color:var(--danger)' : ''}`}>
      <span style="display:flex;align-items:center;gap:.6rem">
        <${Icon} name=${icon} size=${16} />
        <span>${label}</span>
      </span>
      ${value && html`<span class="sub">${value}</span>`}
    </button>`;
}

function Invite({ code, origin }) {
  const url = `${origin}/?invite=${encodeURIComponent(code)}`;
  const svg = useQrSvg(url);
  return html`
    <div class="card" data-testid="invite" style="text-align:center">
      <p class="sub" style="margin:0 0 .6rem">
        Point their camera at this. It works once, for seven days.
      </p>
      <div class="qr" data-testid="invite-qr"
           dangerouslySetInnerHTML=${{ __html: svg || '' }}></div>
      <div><code data-testid="invite-code">${code}</code></div>
    </div>`;
}

function LeagueLink({ authState, onChanged }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const linked = authState.league;

  // Two separate books, shown side by side and never merged: the league
  // rating is reproducible from the scraped archive alone.
  if (linked) {
    return html`
      <div class="card" data-testid="league-linked">
        <div class="rowbetween">
          <span class="sub">${`League record — ${linked.name}`}</span>
          <strong class="brassc mono" data-testid="league-elo">${linked.elo}</strong>
        </div>
        <div class="sub" style="margin-top:.25rem">
          ${`${linked.rank ? `#${linked.rank}` : 'unranked'} · ${linked.games} frames`}
        </div>
      </div>`;
  }

  if (!open) {
    return html`<button class="secondary" data-testid="league-link-open"
                        onClick=${() => setOpen(true)}>
                  Link your league record
                </button>`;
  }

  const look = async (value) => {
    setQ(value); setError('');
    if (!value.trim()) { setFound([]); return; }
    try { setFound(await get(`/api/league/search?q=${encodeURIComponent(value)}`)); }
    catch (e) { setError(e.detail || e.message); }
  };
  const link = (key) => async () => {
    setError('');
    try { await post('/api/me/league', { player_key: key }); await onChanged(); }
    catch (e) { setError(e.detail || e.message); }
  };

  return html`
    <div class="stack">
      <label>Find yourself in the league</label>
      <input data-testid="league-search" value=${q} placeholder="Your name"
             onInput=${(e) => look(e.target.value)} />
      ${found.map((p) => html`
        <div class="card rowbetween" key=${p.player_key}>
          <span>${p.name} <span class="sub">${p.elo}</span></span>
          <button class="secondary" data-testid="league-pick"
                  style="width:auto;margin:0" onClick=${link(p.player_key)}>
            That is me
          </button>
        </div>`)}
      ${error && html`<p class="error">${error}</p>`}
    </div>`;
}

/**
 * Starting a league.
 *
 * Offered only to somebody holding the league administrator role, which is
 * handed out by whoever runs the instance — the chairman of a real local
 * league, who is not thereby given the keys to everything else in it. Lives
 * here, under Manage, rather than on the Leagues tab itself: starting one is
 * an administrative act, not something an everyday visit to Leagues should
 * hand you off to mid-task.
 *
 * "Run here" or "run elsewhere" is asked at the start because it is the one
 * thing about a league that cannot be worked out later: it decides whether
 * the fixtures and results in here are the book, or a window onto somebody
 * else's.
 *
 * Success takes you to the league you just made, rather than just clearing
 * the field. On the Leagues tab that clear was enough, because the list
 * right below it re-read itself and showed the new row -- there is no list
 * here to do that, so a cleared field with nothing else changing is
 * indistinguishable from nothing having happened.
 */
function StartLeague({ onGo }) {
  const [name, setName] = useState('');
  const [source, setSource] = useState('cueforge');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const start = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setFailed('');
    try {
      const made = await post('/api/leagues', { name: name.trim(), source });
      setName('');
      onGo('league', made.league_id);
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="card stack" data-testid="leagues-start">
      <label>Start a league</label>
      <input data-testid="league-name" value=${name} maxlength="80"
             placeholder="Basingstoke and District"
             onInput=${(e) => setName(e.target.value)} />
      <div class="ways">
        ${[['cueforge', 'Run here'], ['external', 'Run elsewhere']]
          .map(([id, label]) => html`
            <button key=${id} class=${source === id ? '' : 'secondary'}
                    data-testid=${`league-source-${id}`}
                    onClick=${() => setSource(id)}>${label}</button>`)}
      </div>
      <p class="sub">
        ${source === 'cueforge'
          ? 'Fixtures and results are kept here.'
          : 'A window onto a league run elsewhere — its own records stay the book.'}
      </p>
      <button data-testid="league-create" disabled=${busy || !name.trim()}
              onClick=${start}>${busy ? 'Starting…' : 'Start it'}</button>
      ${failed && html`<p class="error" data-testid="league-create-error">
                         ${failed}
                       </p>`}
    </div>`;
}

export function SettingsTab({ authState, onChanged, onReload, onGo, build }) {
  const [invite, setInvite] = useState('');
  // Text size lives on the document, not in state; this is only so the picker
  // redraws with the new one selected.
  const [, setRound] = useState(0);
  const [error, setError] = useState('');
  // Shut until asked for. The control almost nobody touches should not be
  // a text box sitting open beside your name inviting an accidental edit.
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');

  const rename = async () => {
    setError('');
    try {
      await put('/api/me/name', { name: draft.trim() });
      setRenaming(false);
      // `onReload` rather than a local echo: the name lives in the auth
      // state that every other screen reads, and a name that changed only
      // on the screen that changed it reads as saved when it is not.
      await onReload();
    } catch (e) { setError(e.detail || e.message); }
  };

  const makeInvite = async () => {
    setError('');
    try { setInvite((await post('/api/invites', {})).code); }
    catch (e) { setError(e.detail || e.message); }
  };

  return html`
    <div style="display:flex;align-items:center;gap:.75rem">
      ${/* Your face, at the size the ball already occupied, on the screen
            you chose it from. This drew the plain ball whatever you had,
            which is why an approved picture read as a lost one: the only
            two places one was ever drawn are the circle roster and the
            members list under Manage, and nobody opens a circle to check
            whether their own upload worked. `Picture` draws nothing
            without one, so everybody who has not chosen a picture -- very
            nearly everybody -- gets the same ball as before. */''}
      <div data-testid="settings-face">
        ${authState.avatar
          ? html`<${Picture} imageId=${authState.avatar} alt=${authState.name}
                             className="avatar"
                             style="width:3rem;height:3rem" />`
          : html`<div class="ball" style="width:3rem;height:3rem"></div>`}
      </div>
      <div style="flex:1">
        <div style="font-weight:700;font-size:1rem" data-testid="settings-name">
          ${authState.name}
        </div>
        ${/* GitHub issue found by auditing every endpoint against every
              call the UI makes: `PUT /api/me/name` had existed all along
              and nothing ever called it, so a name typed wrong on a phone
              -- or guessed by the archive import -- was permanent short of
              an admin editing the name store by hand. */''}
        ${renaming
          ? html`
            <div class="row" style="margin-top:.35rem">
              <input data-testid="settings-name-input" maxlength="80"
                     value=${draft} autofocus
                     onInput=${(e) => setDraft(e.target.value)} />
              <button class="secondary" style="width:auto"
                      data-testid="settings-name-save"
                      disabled=${!draft.trim()} onClick=${rename}>Save</button>
              <button class="secondary" style="width:auto"
                      data-testid="settings-name-cancel"
                      onClick=${() => setRenaming(false)}>Cancel</button>
            </div>`
          : html`
            <button class="linkish sub" data-testid="settings-rename"
                    style="padding:0;background:none;width:auto;text-align:left"
                    onClick=${() => { setDraft(authState.name || '');
                                      setRenaming(true); }}>
              Cueforge · change name
            </button>`}
      </div>
    </div>

    ${/* Beside your name and your face, NOT under Manage. It sat inside
          the `can_admin || may_create_league` block below, so the only
          people who could choose a picture were the same people who
          approve them -- an ordinary member had no control at all, and
          the ones who tested it were all admins looking at an admin's
          account. The server's rule is unchanged and stricter than this
          screen: an avatar's subject must be the person choosing it, and
          not even an admin may pick one for somebody else. Issue #66. */''}
    <div class="stack">
      <${ImageUpload} kind="avatar" subjectId=${authState.user_id}
                      label="Your picture" />
    </div>

    <${LeagueLink} authState=${authState} onChanged=${onReload} />

    <${Devices} />

    ${(authState.can_admin || authState.may_create_league) && html`
      <div class="stack">
        <label>Manage</label>
        ${authState.can_admin && html`
          <${Row} icon="shield" label="Admin" testid="settings-admin"
                  onClick=${() => { location.hash = '#/admin'; }} />`}
        ${/* The way in for whoever runs a league. Not shown to anybody
              else: a Start button that answers "you may not" is worse
              than none. */''}
        ${authState.may_create_league && html`<${StartLeague} onGo=${onGo} />`}
      </div>`}

    ${authState.is_admin && html`
      <div class="stack">
        <label>Bring somebody in</label>
        <${Row} icon="qr-code" label="Invite someone" testid="home-invite"
                onClick=${makeInvite} />
        ${invite && html`<${Invite} code=${invite} origin=${location.origin} />`}
      </div>`}

    ${/* Its own section, above the game: it is the thing somebody goes
          looking for when they cannot read the rest of the screen. */''}
    <${Notifications} />

    <${TextSize} onChanged=${() => setRound((r) => r + 1)} />
    ${/* Beside the text size — the other thing here that changes how
          the app looks rather than what it does. Issue #52. */''}
    <${WidthMode} onChanged=${() => setRound((r) => r + 1)} />

    <div class="stack">
      <label>The game</label>
      <${Row} icon="help-circle" label="Blackball rules" testid="settings-rules"
              onClick=${() => onGo('rules')} />
      ${/* A static page under the served web dir, not an in-app screen --
            opened in its own tab so it survives a reload of the guide
            without losing the app underneath it. */''}
      <${Row} icon="help-circle" label="How to use Cueforge"
              testid="settings-guide"
              onClick=${() => window.open('/guide/', '_blank', 'noopener')} />
    </div>

    <div class="stack">
      <label>Account</label>
      <${Row} icon="log-out" label="Sign out" testid="home-signout" danger=${true}
              onClick=${async () => { await signOut(); await onChanged(); }} />
    </div>

    <p class="sub mono" data-testid="build"
       style="text-align:center;opacity:.5;font-size:.625rem">
      ${`CUEFORGE · ${build}`}
    </p>

    ${error && html`<p class="error" data-testid="error">${error}</p>`}`;
}
